import { feathers } from '@/feathers-client';
import { SortOrder } from '@/Core/config/query-mappings';
import { HIDE_ERROR_AFTER_MS } from '@/Core/utilities';
import { ESSearchHeader } from '@/Core/config/es-search-header';

const DefaultDateRange = 30;

/**
 * Controls recent events widget on dashboard.
 */
export default {
  namespaced: true,
  state: {
    events: [],
    eventsTotal: 0,
    eventsPages: null,
    eventsError: false,
    eventsErrorMessage: '',
    eventsSuccessMessage: '',
    eventsInProgress: false,
    lastActivityDate: '',
    comments: [],
    commentsTotal: 0,
    commentsError: false,
    commentsErrorMessage: '',
    commentsInProgress: false,
  },
  mutations: {
    setEvents(state, events) {
      state.events = events;
    },
    eventsInProgress(state) {
      state.eventsInProgress = true;
      state.eventsError = false;
    },
    eventsSuccess(state, events) {
      state.events = events.data || events;
      state.eventsTotal = events.total || events.length;
      state.eventsPages = Math.ceil(state.eventsTotal / 10) || 0;
      state.eventsError = false;
      state.eventsInProgress = false;
    },
    setEventsSuccessMessage(state, message) {
      state.eventsSuccessMessage = message;
    },
    eventsError(state, error) {
      state.eventsError = true;
      state.eventsInProgress = false;
      state.eventsErrorMessage = error.message;
    },
    clearErrors(state) {
      state.eventsError = false;
      state.eventsErrorMessage = '';
    },
    clearInProgress(state) {
      state.eventsInProgress = false;
    },
    setLastActivityDate(state, createdDate) {
      state.lastActivityDate = createdDate;
    },
    setComments(state, comments) {
      state.comments = comments;
    },
    commentsInProgress(state) {
      state.commentsInProgress = true;
      state.commentsError = false;
    },
    commentsSuccess(state, comments) {
      state.comments = comments.data || comments;
      state.commentsTotal = comments.total || comments.length;
      state.commentsError = false;
      state.commentsInProgress = false;
    },
    commentsError(state, error) {
      state.commentsError = true;
      state.commentsInProgress = false;
      state.commentsErrorMessage = error.message;
    },
  },
  getters: {
    getLastActivityDate: state => {
      return state.lastActivityDate;
    },
  },
  actions: {
    async showEventsError({ commit }, message) {
      commit('eventsError', { message });
      setTimeout(() => {
        commit('clearErrors');
      }, HIDE_ERROR_AFTER_MS);
    },
    async getEvents({ commit, rootState }, querySettings) {
      const { page, sortBy, descending } = querySettings.pagination;
      let pageSize = rootState.preferences.eventsPageSize;
      const { isProfileV2, isDashboard, memberID, dateRange, isComments } = querySettings;
      if (isProfileV2 && pageSize < 15) pageSize = 15;

      if (isComments) {
        commit('commentsInProgress');
      } else {
        commit('eventsInProgress');
      }
      
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - DefaultDateRange);
      startDate.setHours(0, 0, 0, 0);
      const query = {
        created_at: { $gte: startDate },
        type: { $in: querySettings.types },
        $sort: { pinned: -1, created_at: -1 },
        $limit: pageSize,
      };

      query.$sort[sortBy] = descending ? SortOrder.desc : SortOrder.asc;
      query.$skip = pageSize * (page - 1);

      if (querySettings.allWebTypes) {
        delete query.type;
      }

      let eventsEndpoint = 'events/search';

      if (querySettings.contactId) {
        query.contact_id = querySettings.contactId;
        query.teams = true;
        query.$load = 'user';
        delete query.created_at;
      }

      if (dateRange) {
        const createdAt = new Date();
        createdAt.setDate(createdAt.getDate() - dateRange);
        createdAt.setHours(0, 0, 0, 0);
        query.created_at = { $gte: createdAt };
      }

      // disable pagination for dashboard table
      if (isDashboard) {
        query.$paginate = 'false';
        query.$load = 'contact';
        query.$sort = { created_at: -1 };
        delete query.created_at;
        eventsEndpoint = 'dashboard-events';
      }

      let eventsResponse;
      try {
        if (isDashboard) {
          eventsResponse = await feathers.service(eventsEndpoint)
            .find({ query, headers: ESSearchHeader });
        }
        else {
          eventsResponse = await feathers.service(eventsEndpoint)
            .create({
              query: { ...query },
            },
              { headers: ESSearchHeader });
        }
        if (memberID) {
          eventsResponse.data = eventsResponse.data.filter(events => events.user_id == memberID)
        }

        if (isComments) {
          commit('setComments', eventsResponse.data);
        } else {
          commit('setEvents', eventsResponse.data);
        }
        
      } catch (error) {
        if (isComments) {
          commit('commentsError', error);
        } else {
          commit('eventsError', error);
        }
      }
      if (isComments) {
        commit('commentsSuccess', eventsResponse);
      } else {
        commit('eventsSuccess', eventsResponse);
      }
      
    },
    async fetchLastActivityDate({ commit }, querySettings) {
      const { page, } = querySettings.pagination;
      const pageSize = 1;
      const { dateRange } = querySettings;

      commit('eventsInProgress');
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - DefaultDateRange);
      startDate.setHours(0, 0, 0, 0);
      const query = {
        created_at: { $gte: startDate },
        $sort: { created_at: -1 },
        $limit: pageSize,
      };

      query.$skip = pageSize * (page - 1);

      const eventsEndpoint = 'events';

      if (querySettings.contactId) {
        query.contact_id = querySettings.contactId;
        query.teams = true;
        query.$load = 'user';
        delete query.created_at;
      }

      if (dateRange) {
        const createdAt = new Date();
        createdAt.setDate(createdAt.getDate() - dateRange);
        createdAt.setHours(0, 0, 0, 0);
        query.created_at = { $gte: createdAt };
      }

      let eventsResponse;
      try {
        eventsResponse = await feathers.service(eventsEndpoint)
          .find({ query, headers: ESSearchHeader });

        const { created_at } =  eventsResponse.data[0];

        commit('setLastActivityDate', created_at);
      } catch (error) {
        return commit('setLastActivityDate', false);
      }
      commit('eventsSuccess', eventsResponse);
    },
    clearErrors({ commit }) {
      commit('clearErrors');
    },
    async removeEvent({ commit }, payload) {
      const { eventId } = payload;
      try {
        await feathers.service('events').remove(eventId);
      } catch (error) {
        commit('eventsError', error);
      }
    },
    async activateEvent({ commit }, payload) {
      const { eventId } = payload;
      try {
        await feathers.service('events').patch(eventId, { is_active: 1 });
      } catch (error) {
        commit('eventsError', error);
      }
    },
    async updateEvent({ commit }, payload) {
      commit('eventsInProgress');
      const { eventId, data } = payload;
      try {
        await feathers.service('events').patch(eventId, data);
      } catch (error) {
        commit('eventsError', error);
      }
      commit('clearInProgress');
    },
    async updatePinEvent({ commit }, payload) {
      commit('eventsInProgress');
      commit('clearErrors');
      try {
        const { pinned } = payload;
        if (pinned) {
          const { pinned_event_id, event_id } = payload;
          await feathers.service('pinned-events').remove(
            pinned_event_id,
            { query: { event_id } },
          );
          commit('setEventsSuccessMessage', 'Activity unpinned on contact profile.');
        } else {
          await feathers.service('pinned-events').create(payload);
          commit('setEventsSuccessMessage', 'Activity pinned on contact profile.');
        }
      } catch (error) {
        commit('eventsError', error);
      }
      commit('clearInProgress');
    }
  },
};
